import React, { useState } from 'react';
import { ArrowLeft, Sprout, Leaf, TreePine, User, Shield, AtSign } from 'lucide-react';
import { Button, Input } from '../components/ui';

export const AddChild = ({ family, guardian, onNavigate, onChildCreated }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    username: '',
    age: '',
    avatar: 'sprout',
    pin: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app/api';

  const avatarOptions = [
    { id: 'sprout', name: 'Sprout', icon: Sprout, color: 'emerald' },
    { id: 'leaf', name: 'Leaf', icon: Leaf, color: 'green' },
    { id: 'tree', name: 'Tree', icon: TreePine, color: 'teal' }
  ];

  const avatarColors = {
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-500', icon: 'bg-emerald-500', text: 'text-emerald-700' },
    green: { bg: 'bg-green-50', border: 'border-green-500', icon: 'bg-green-500', text: 'text-green-700' },
    teal: { bg: 'bg-teal-50', border: 'border-teal-500', icon: 'bg-teal-500', text: 'text-teal-700' }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username.trim())) {
      newErrors.username = 'Letters, numbers, and underscores only';
    }

    if (!formData.age) {
      newErrors.age = 'Age is required';
    } else {
      const age = parseInt(formData.age);
      if (isNaN(age)) {
        newErrors.age = 'Age must be a number';
      }
    }

    if (!formData.pin) {
      newErrors.pin = 'PIN is required';
    } else if (formData.pin.length !== 4) {
      newErrors.pin = 'PIN must be exactly 4 digits';
    } else if (!/^\d+$/.test(formData.pin)) {
      newErrors.pin = 'PIN must contain only numbers';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await fetch(`${API_BASE_URL}/families/${family.id}/children`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        if (onChildCreated) {
          onChildCreated(result.data);
        } else {
          onNavigate('familyDashboard');
        }
      }
    } catch (error) {
      if (error.message.includes('PIN')) {
        setErrors({ pin: error.message });
      } else if (error.message.includes('username') || error.message.includes('Username')) {
        setErrors({ username: error.message });
      } else if (error.message.includes('age')) {
        setErrors({ age: error.message });
      } else {
        setErrors({ general: error.message || 'Failed to create child account' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedAvatar = avatarOptions.find(opt => opt.id === formData.avatar);
  const selectedColors = avatarColors[selectedAvatar.color];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-100 bg-white sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => onNavigate('familyDashboard')}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isSubmitting}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Add Child</h1>
                <p className="text-xs text-gray-500">{family?.name || 'Your Family'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">

              {errors.general && (
                <div className="mb-5 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-red-600 text-sm">{errors.general}</p>
                </div>
              )}

              <div className="space-y-5">
                {/* Name & Username */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Child's Name"
                    icon={User}
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    error={errors.firstName}
                    placeholder="Emma"
                    disabled={isSubmitting}
                  />
                  <div>
                    <Input
                      label="Username"
                      icon={AtSign}
                      value={formData.username}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 20);
                        setFormData({ ...formData, username: value });
                        setErrors({ ...errors, username: undefined });
                      }}
                      error={errors.username}
                      placeholder="emma_sprout"
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Used for login
                    </p>
                  </div>
                </div>

                {/* Age */}
                <div className="w-1/2">
                  <Input
                    label="Age"
                    type="number"
                    value={formData.age}
                    onChange={(e) => {
                      setFormData({ ...formData, age: e.target.value });
                      setErrors({ ...errors, age: undefined });
                    }}
                    error={errors.age}
                    placeholder="8"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Avatar */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Avatar
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {avatarOptions.map((option) => {
                      const IconComponent = option.icon;
                      const isSelected = formData.avatar === option.id;
                      const colors = avatarColors[option.color];

                      return (
                        <button
                          key={option.id}
                          onClick={() => {
                            setFormData({ ...formData, avatar: option.id });
                            setErrors({ ...errors, avatar: undefined });
                          }}
                          className={`
                            p-3 rounded-xl border text-center transition-colors
                            ${isSelected
                              ? `${colors.border} ${colors.bg}`
                              : 'border-gray-200 hover:border-gray-300'
                            }
                            ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
                          `}
                          disabled={isSubmitting}
                        >
                          <div className={`w-10 h-10 mx-auto mb-2 ${colors.icon} rounded-xl flex items-center justify-center`}>
                            <IconComponent className="w-5 h-5 text-white" />
                          </div>
                          <div className={`text-sm font-medium ${isSelected ? colors.text : 'text-gray-700'}`}>
                            {option.name}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* PIN */}
                <div className="w-1/2">
                  <Input
                    label="4-Digit PIN"
                    type="password"
                    icon={Shield}
                    value={formData.pin}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setFormData({ ...formData, pin: value });
                      setErrors({ ...errors, pin: undefined });
                    }}
                    error={errors.pin}
                    placeholder="- - - -"
                    maxLength="4"
                    disabled={isSubmitting}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Your child will use this to sign in
                  </p>
                </div>

                {/* Submit */}
                <div className="pt-4 border-t border-gray-100 flex gap-3">
                  <Button
                    onClick={handleSubmit}
                    size="lg"
                    className="flex-1"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Creating...' : 'Add Child'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="lg"
                    onClick={() => onNavigate('familyDashboard')}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sticky top-20">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">Preview</p>

              <div className={`${selectedColors.bg} rounded-xl p-5 text-center`}>
                <div className={`w-14 h-14 mx-auto mb-3 ${selectedColors.icon} rounded-xl flex items-center justify-center`}>
                  <selectedAvatar.icon className="w-7 h-7 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-0.5">
                  {formData.firstName || 'Child'}'s Garden
                </h4>
                <p className="text-sm text-gray-500 mb-3">
                  {formData.username ? `@${formData.username}` : 'No username yet'}
                  {formData.age ? ` · Age ${formData.age}` : ''}
                </p>
                <div className="bg-white/80 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-900">$0.00</div>
                  <div className="text-xs text-gray-500">Starting Balance</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};